import React, { useState } from 'react';
import { MoreVertical, GitCompare, RotateCcw, Upload, Eye, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface Version {
  id: string;
  version_number: number;
  created_at?: string;
  commit_message?: string;
  is_draft?: boolean;
}

interface VersionHistoryProps {
  versions: Version[];
  publishedVersionId?: string;
  currentVersionNumber?: number;  // Version currently being viewed
  isViewingReadOnlyVersion?: boolean;  // True when viewing a read-only version tab
  onCompare: (v1: number, v2: number) => void;
  onRestore: (versionNumber: number) => void;
  onPublish: (versionId: string) => void;
  onViewVersion?: (version: Version) => void;
  onBackToDraft?: () => void;  // Called when user wants to go back to editing the draft
}

function VersionItem({
  version,
  isPublished,
  isCurrentVersion,
  selected,
  onSelect,
  onAction,
}: {
  version: Version;
  isPublished: boolean;
  isCurrentVersion: boolean;
  selected: boolean;
  onSelect: (versionNumber: number) => void;
  onAction: (action: string, version: Version) => void;
}) {
  const formatDate = (isoString?: string) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2 py-2 hover:bg-muted/50',
        selected && 'bg-muted',
        isPublished && 'border-l-2 border-green-500'
      )}
    >
      <Checkbox
        checked={selected}
        onCheckedChange={() => onSelect(version.version_number)}
        className="h-4 w-4"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">v{version.version_number}</span>
          {isCurrentVersion && (
            <Badge variant="secondary" className="text-xs">
              Current
            </Badge>
          )}
          {isPublished && (
            <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
              Published
            </Badge>
          )}
          {version.is_draft && (
            <Badge variant="outline" className="text-yellow-600 border-yellow-600 text-xs">
              Draft
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{formatDate(version.created_at)}</p>
        {version.commit_message && (
          <p className="text-xs text-muted-foreground truncate">{version.commit_message}</p>
        )}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-6 w-6">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!isCurrentVersion && (
            <DropdownMenuItem onClick={() => onAction('view', version)}>
              <Eye className="h-4 w-4 mr-2" />
              View
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => onAction('restore', version)}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Restore
          </DropdownMenuItem>
          {!isPublished && (
            <DropdownMenuItem onClick={() => onAction('publish', version)}>
              <Upload className="h-4 w-4 mr-2" />
              Publish
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function VersionHistory({
  versions = [],
  publishedVersionId,
  currentVersionNumber,
  isViewingReadOnlyVersion,
  onCompare,
  onRestore,
  onPublish,
  onViewVersion,
  onBackToDraft,
}: VersionHistoryProps) {
  const [selectedVersions, setSelectedVersions] = useState<number[]>([]);

  const handleSelectVersion = (versionNumber: number) => {
    setSelectedVersions((prev) => {
      if (prev.includes(versionNumber)) {
        return prev.filter((v) => v !== versionNumber);
      }
      if (prev.length >= 2) {
        return [prev[1], versionNumber];
      }
      return [...prev, versionNumber];
    });
  };

  const handleCompare = () => {
    if (selectedVersions.length === 2) {
      const [v1, v2] = selectedVersions.sort((a, b) => a - b);
      onCompare(v1, v2);
    }
  };

  const handleAction = (action: string, version: Version) => {
    switch (action) {
      case 'view':
        if (onViewVersion) {
          onViewVersion(version);
        }
        break;
      case 'restore':
        onRestore(version.version_number);
        break;
      case 'publish':
        onPublish(version.id);
        break;
    }
  };

  const isVersionPublished = (version: Version) => version.id === publishedVersionId;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h3 className="font-semibold text-sm">Version History</h3>
        <span className="text-xs text-muted-foreground">
          {versions.length} version{versions.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Compare Button */}
      {selectedVersions.length === 2 && (
        <div className="p-2 border-b">
          <Button size="sm" className="w-full" onClick={handleCompare}>
            <GitCompare className="h-4 w-4 mr-2" />
            Compare v{Math.min(...selectedVersions)} with v{Math.max(...selectedVersions)}
          </Button>
        </div>
      )}

      {/* Help Text */}
      {selectedVersions.length < 2 && (
        <div className="px-4 py-2 bg-muted/50">
          <p className="text-xs text-muted-foreground">Select 2 versions to compare</p>
        </div>
      )}

      {/* Back to Draft Button - shown when viewing a read-only version */}
      {isViewingReadOnlyVersion && onBackToDraft && (
        <div className="p-2 border-b">
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={onBackToDraft}
          >
            <Edit className="h-4 w-4 mr-2" />
            Back to Draft
          </Button>
        </div>
      )}

      {/* Version List */}
      <div className="flex-1 overflow-auto">
        {versions.map((version) => (
          <VersionItem
            key={version.id}
            version={version}
            isPublished={isVersionPublished(version)}
            isCurrentVersion={version.version_number === currentVersionNumber}
            selected={selectedVersions.includes(version.version_number)}
            onSelect={handleSelectVersion}
            onAction={handleAction}
          />
        ))}
        {versions.length === 0 && (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-muted-foreground">No versions yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default VersionHistory;
